<template>
  <div class="p-3 wasi-panel">

    <soft-alert v-if="alert.state" :color="alert.color" class="mb-3">
      {{ alert.message }}
    </soft-alert>

    <!-- ============ SELECTOR DE USUARIO DEL CRM ============ -->
    <div class="wasi-user mb-3">
      <div v-if="selectedUser" class="wasi-user__chip">
        <div class="wasi-user__info">
          <span class="wasi-user__name">{{ userLabel(selectedUser) }}</span>
          <span class="wasi-user__mail">{{ selectedUser.email }}</span>
        </div>
        <soft-button
          type="button"
          color="secondary"
          variant="outline"
          size="sm"
          :disabled="isRunning || isLoadingInit"
          @click="clearUser"
        >
          Cambiar
        </soft-button>
      </div>

      <template v-else>
        <label class="form-label fw-semibold">Usuario del CRM</label>
        <soft-input
          v-model:value="userQuery"
          type="text"
          placeholder="Buscar por nombre o correo..."
          aria-label="Buscar usuario del CRM"
        />
        <div v-if="isSearchingUsers" class="text-muted small mt-1">
          <i class="fas fa-spinner fa-spin me-1"></i> Buscando...
        </div>
        <ul v-else-if="userResults.length" class="wasi-user__list mt-2">
          <li v-for="u in userResults" :key="u.id" @click="selectUser(u)">
            <span class="wasi-user__name">{{ userLabel(u) }}</span>
            <span class="wasi-user__mail">{{ u.email }}</span>
          </li>
        </ul>
        <p v-else-if="userSearched" class="text-muted small mt-2 mb-0">
          Sin resultados para "{{ userQuery }}".
        </p>
        <small v-else class="text-muted">
          Elige el usuario al que se conectan las credenciales y se le asignan los
          inmuebles importados.
        </small>
      </template>
    </div>

    <!-- Cargando estado inicial -->
    <div v-if="!selectedUser" class="wasi-empty">
      <i class="fas fa-user-check me-2"></i>
      Selecciona primero un usuario para ver su conexión con Wasi.
    </div>

    <div v-else-if="isLoadingInit" class="text-center py-4 text-muted">
      <i class="fas fa-spinner fa-spin me-2"></i> Consultando estado de la conexión...
    </div>

    <template v-else>

      <!-- ============ ESTADO: SIN CREDENCIALES ============ -->
      <form v-if="!credentials.connected" @submit.prevent="saveCredentials">
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label fw-semibold">ID de compañía (id_company)</label>
            <soft-input
              v-model:value="idCompany"
              type="text"
              placeholder="1234567890"
              aria-label="ID de compañía de Wasi"
              required
            />
            <small class="text-muted">
              Lo encuentras en Wasi: Configuración → Ajustes generales → API de Wasi.
            </small>
          </div>

          <div class="col-12">
            <label class="form-label fw-semibold">Token (wasi_token)</label>
            <soft-input
              v-model:value="wasiToken"
              type="text"
              placeholder="XXXX_XXXX_XXXX_XXXX"
              aria-label="Token de Wasi"
              required
            />
          </div>

          <div class="col-12">
            <label class="form-label fw-semibold">Estado al importar</label>
            <div class="wasi-state">
              <label
                v-for="opt in importStateOptions"
                :key="opt.value"
                class="wasi-state__opt"
              >
                <input type="radio" :value="opt.value" v-model="importState" />
                <span>
                  <strong>{{ opt.label }}</strong>
                  <small>{{ opt.hint }}</small>
                </span>
              </label>
            </div>
            <small class="text-muted">
              Solo aplica a los inmuebles nuevos. En las resincronizaciones no se toca el
              estado de los que ya se importaron.
            </small>
          </div>
        </div>

        <!-- Resultado de la prueba de conexión -->
        <div v-if="testResult" class="wasi-test mt-3">
          <i class="fas fa-check-circle me-2"></i>
          <span>
            <strong>{{ testResult.company_name }}</strong> —
            {{ testResult.total_properties }} propiedades listas para importar.
          </span>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-4">
          <soft-button
            type="button"
            color="secondary"
            variant="outline"
            size="sm"
            :loading="isTesting"
            :disabled="!canSubmit"
            @click="testConnection"
          >
            <i class="fas fa-plug me-1"></i> Probar conexión
          </soft-button>

          <soft-button
            type="submit"
            color="primary"
            variant="gradient"
            size="sm"
            :loading="isSaving"
            :disabled="!canSubmit"
          >
            <i class="fas fa-save me-1"></i> Guardar
          </soft-button>
        </div>
      </form>

      <!-- ============ ESTADO: CONECTADO ============ -->
      <div v-else>
        <div class="wasi-conn">
          <div class="wasi-conn__row">
            <span class="wasi-conn__label">Empresa</span>
            <span class="wasi-conn__value">{{ credentials.company_name || '—' }}</span>
          </div>
          <div class="wasi-conn__row">
            <span class="wasi-conn__label">ID de compañía</span>
            <span class="wasi-conn__value">{{ credentials.id_company }}</span>
          </div>
          <div class="wasi-conn__row">
            <span class="wasi-conn__label">Token</span>
            <span class="wasi-conn__value font-monospace">{{ credentials.wasi_token_masked }}</span>
          </div>
          <div class="wasi-conn__row">
            <span class="wasi-conn__label">Estado al importar</span>
            <span class="wasi-conn__value">{{ importStateLabel }}</span>
          </div>
          <div class="wasi-conn__row">
            <span class="wasi-conn__label">Conectado desde</span>
            <span class="wasi-conn__value">{{ credentials.connected_at || '—' }}</span>
          </div>
        </div>

        <!-- Importación en curso -->
        <div v-if="isRunning" class="wasi-progress mt-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="wasi-progress__text">
              <i class="fas fa-spinner fa-spin me-2"></i>
              <template v-if="importStatus.status === 'queued'">En cola...</template>
              <template v-else>Importando... {{ importStatus.processed || 0 }} de {{ importStatus.total || 0 }}</template>
            </span>
            <span class="wasi-progress__pct">{{ progressPct }}%</span>
          </div>
          <div class="wasi-bar">
            <div class="wasi-bar__fill" :style="{ width: progressPct + '%' }"></div>
          </div>
          <small class="text-muted d-block mt-2">
            Puedes cerrar esta ventana, el proceso sigue corriendo en el servidor.
          </small>
        </div>

        <!-- Resumen de la última importación -->
        <div v-else-if="importStatus.status === 'completed'" class="mt-3">
          <soft-alert :color="importStatus.errors > 0 ? 'warning' : 'success'">
            <template v-if="importStatus.errors > 0">
              Se importaron {{ importStatus.imported }} inmuebles.
              {{ importStatus.errors }} no se pudieron procesar.
            </template>
            <template v-else>
              Última importación: {{ importStatus.imported }} nuevas,
              {{ importStatus.updated }} actualizadas,
              {{ importStatus.deleted }} marcadas como eliminadas
              ({{ importStatus.finished_at }}).
            </template>
          </soft-alert>
        </div>

        <div v-else-if="importStatus.status === 'failed'" class="mt-3">
          <soft-alert color="danger">
            La última importación falló. Vuelve a intentarlo o revisa tus credenciales.
          </soft-alert>
        </div>

        <p class="wasi-help mt-3">
          <i class="fas fa-info-circle me-1"></i>
          Traeremos tus inmuebles con sus propietarios, fotos y características.
          Lo que edites aquí no se sobrescribe en las siguientes sincronizaciones.
          Una vez conectado, el CRM revisa Wasi cada noche automáticamente.
        </p>

        <div class="wasi-limit mt-3">
          <label for="wasi-limit" class="wasi-limit__label">Límite de prueba</label>
          <input
            id="wasi-limit"
            v-model="importLimit"
            type="number"
            min="1"
            class="wasi-limit__input"
            placeholder="todos"
            :disabled="isRunning"
          />
          <small class="text-muted">
            Opcional. Procesa solo esa cantidad de inmuebles y no marca eliminados.
            Déjalo vacío para importar el inventario completo.
          </small>
        </div>

        <div class="d-flex justify-content-between gap-2 mt-4">
          <soft-button
            type="button"
            color="danger"
            variant="outline"
            size="sm"
            :disabled="isRunning"
            @click="confirmDisconnect = true"
          >
            <i class="fas fa-unlink me-1"></i> Desconectar
          </soft-button>

          <soft-button
            type="button"
            color="primary"
            variant="gradient"
            size="sm"
            :loading="isImporting"
            :disabled="isRunning"
            @click="runImport"
          >
            <i class="fas fa-cloud-download-alt me-1"></i> Importar ahora
          </soft-button>
        </div>

        <!-- Confirmación de desconexión -->
        <div v-if="confirmDisconnect" class="wasi-confirm mt-3">
          <p class="mb-3">
            Se dejará de sincronizar con Wasi. Los inmuebles ya importados seguirán en tu CRM,
            pero dejarán de actualizarse.
          </p>
          <div class="d-flex justify-content-end gap-2">
            <soft-button type="button" color="secondary" variant="outline" size="sm"
              @click="confirmDisconnect = false">
              Cancelar
            </soft-button>
            <soft-button type="button" color="danger" variant="gradient" size="sm"
              :loading="isDisconnecting" @click="disconnect">
              Sí, desconectar
            </soft-button>
          </div>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import SoftInput from '@/components/SoftInput.vue';
import SoftButton from '@/components/SoftButton.vue';
import SoftAlert from '@/components/SoftAlert.vue';

const POLL_MS = 5000;

// inmuebles_states: 2 Activo (por defecto) | 3 Eliminado
const DEFAULT_STATE = 2;

export default {
  name: 'FormWasi',
  components: { SoftInput, SoftButton, SoftAlert },
  setup() {
    const store = useStore();

    // Usuario del CRM al que se le conectan las credenciales (obligatorio en la API)
    const selectedUser     = ref(null);
    const userQuery        = ref('');
    const userResults      = ref([]);
    const isSearchingUsers = ref(false);
    const userSearched     = ref(false);

    const idCompany   = ref('');
    const wasiToken   = ref('');
    const importState = ref(DEFAULT_STATE); // id de inmuebles_states: 2 | 3
    const importLimit = ref('');         // opcional, solo para pruebas

    const isLoadingInit   = ref(false);
    const isTesting       = ref(false);
    const isSaving        = ref(false);
    const isImporting     = ref(false);
    const isDisconnecting = ref(false);

    const testResult        = ref(null);
    const confirmDisconnect = ref(false);
    const alert = ref({ state: false, message: '', color: 'success' });

    let pollTimer = null;

    const credentials  = computed(() => store.state.appWasi?.credentials || { connected: false });
    const importStatus = computed(() => store.state.appWasi?.importStatus || { status: 'never' });

    const isRunning = computed(() =>
      ['queued', 'running'].includes(importStatus.value.status)
    );

    const progressPct = computed(() => {
      const { processed = 0, total = 0 } = importStatus.value;
      if (!total) return 0;
      return Math.min(100, Math.round((processed / total) * 100));
    });

    // ---------- Estados de inmueble (GET /auth/api-inmueble-state) ----------

    // Wasi guarda el id tal cual en inmuebles.state_inmueble y solo acepta 2 o 3,
    // asi que del catalogo nos quedamos con esos dos.
    const STATE_HINTS = {
      2: 'Los inmuebles entran publicados y listos para usar.',
      3: 'Entran eliminados, para revisarlos y activarlos a mano.',
    };


    const FALLBACK_STATES = [
      { value: 2, label: 'Activo', hint: STATE_HINTS[2] },
      { value: 3, label: 'Eliminado', hint: STATE_HINTS[3] },
    ];

    const importStateOptions = computed(() => {
      const catalog = store.state.appHelpers.propertyStates || [];
      const options = catalog
        .map(st => ({ value: Number(st.id), label: st.name, hint: STATE_HINTS[st.id] }))
        .filter(opt => opt.value in STATE_HINTS);
      return options.length ? options : FALLBACK_STATES;
    });

    const importStateLabel = computed(() => {
      const current = Number(credentials.value.state_inmueble) || DEFAULT_STATE;
      return importStateOptions.value.find(o => o.value === current)?.label || current;
    });

    const loadPropertyStates = () => {
      if (store.state.appHelpers.propertyStates?.length) return;
      store.dispatch('appHelpers/storePropertyStates').catch(err => console.error(err));
    };

    const userId = computed(() => selectedUser.value?.id || null);

    const canSubmit = computed(() =>
      !!userId.value && !!idCompany.value && !!wasiToken.value
    );

    const showAlert = (message, color = 'success', timeout = 8000) => {
      alert.value = { state: true, message, color };
      setTimeout(() => { alert.value.state = false; }, timeout);
    };

    const errorMessage = (err, fallback) =>
      err?.response?.data?.errorMessage
      || err?.response?.data?.message
      || fallback;

    // data.code: 'credentials' | 'plan' | 'unreachable' -> el plan no es un error del usuario
    const errorColor = (err) =>
      err?.response?.data?.data?.code === 'plan' ? 'warning' : 'danger';

    const stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    const startPolling = () => {
      stopPolling();
      pollTimer = setInterval(async () => {
        try {
          await store.dispatch('appWasi/storeImportStatusWasi', { userId: userId.value });
          if (!isRunning.value) {
            stopPolling();
            if (importStatus.value.status === 'completed') {
              showAlert(
                `Importación completada: ${importStatus.value.imported} nuevas, `
                + `${importStatus.value.updated} actualizadas, `
                + `${importStatus.value.deleted} marcadas como eliminadas.`,
                importStatus.value.errors > 0 ? 'warning' : 'success'
              );
            }
          }
        } catch (err) {
          stopPolling();
          showAlert(errorMessage(err, 'No se pudo consultar el estado de la importación.'), 'danger');
        }
      }, POLL_MS);
    };

    const loadImportStatus = async () => {
      try {
        await store.dispatch('appWasi/storeImportStatusWasi', { userId: userId.value });
        if (isRunning.value) startPolling();
      } catch (err) {
        console.error(err);
      }
    };

    const init = async () => {
      if (!userId.value) return;
      loadPropertyStates();
      isLoadingInit.value = true;
      try {
        await store.dispatch('appWasi/storeGetCredentialsWasi', userId.value);
        if (credentials.value.connected) {
          importState.value = Number(credentials.value.state_inmueble) || DEFAULT_STATE;
          await loadImportStatus();
        }
      } catch (err) {
        showAlert(errorMessage(err, 'No se pudo consultar el estado de la conexión.'), 'danger');
      } finally {
        isLoadingInit.value = false;
      }
    };

    // test-connection ignora state_inmueble, pero enviarlo no molesta y evita dos payloads
    const payload = () => ({
      user_id: userId.value,
      id_company: idCompany.value,
      wasi_token: wasiToken.value,
      state_inmueble: importState.value,
    });

    const testConnection = async () => {
      isTesting.value = true;
      testResult.value = null;
      try {
        const res = await store.dispatch('appWasi/storeTestConnectionWasi', payload());
        testResult.value = res.data;
        showAlert(res.message || 'Conexión exitosa.', 'success');
      } catch (err) {
        showAlert(errorMessage(err, 'No se pudo conectar con Wasi.'), errorColor(err));
      } finally {
        isTesting.value = false;
      }
    };

    const saveCredentials = async () => {
      isSaving.value = true;
      try {
        const res = await store.dispatch('appWasi/storeCredentialsWasi', payload());
        showAlert(res.message || 'Credenciales guardadas correctamente.', 'success');
        idCompany.value = '';
        wasiToken.value = '';
        testResult.value = null;
        await loadImportStatus();

      } catch (err) {
        showAlert(errorMessage(err, 'No se pudieron guardar las credenciales.'), errorColor(err));
      } finally {
        isSaving.value = false;
      }
    };

    const runImport = async () => {
      isImporting.value = true;
      try {
        const res = await store.dispatch('appWasi/storeRunImportWasi', {
          userId: userId.value,
          limit: Number(importLimit.value) || null,
        });
        showAlert(res.message || 'Importación iniciada.', 'success');
        await store.dispatch('appWasi/storeImportStatusWasi', {
          userId: userId.value,
          importId: res.data?.import_id || null,
        });
        startPolling();
      } catch (err) {
        // 409: ya hay una importación en curso -> no es error, seguimos el progreso
        if (err?.response?.status === 409) {
          await store.dispatch('appWasi/storeImportStatusWasi', {
            userId: userId.value,
            importId: err.response.data?.data?.import_id || null,
          }).catch(e => console.error(e));
          startPolling();
        } else {
          showAlert(errorMessage(err, 'No se pudo iniciar la importación.'), 'danger');
        }
      } finally {
        isImporting.value = false;
      }
    };

    const disconnect = async () => {
      isDisconnecting.value = true;
      try {
        const res = await store.dispatch('appWasi/storeDisconnectWasi', userId.value);
        stopPolling();
        confirmDisconnect.value = false;
        showAlert(res.message || 'Wasi desconectado.', 'success');
      } catch (err) {
        showAlert(errorMessage(err, 'No se pudo desconectar Wasi.'), 'danger');
      } finally {
        isDisconnecting.value = false;
      }
    };

    // ---------- Selección del usuario del CRM ----------

    const userLabel = (u) =>
      u?.name || [u?.first_name, u?.last_name].filter(Boolean).join(' ') || u?.email || '—';

    const searchUsers = async () => {
      const query = userQuery.value.trim();
      if (query.length < 3) {
        userResults.value = [];
        userSearched.value = false;
        return;
      }
      isSearchingUsers.value = true;
      try {
        const res = await store.dispatch('appLonjas/searchMainUsersAction', { query });
        userResults.value = res?.data || res || [];
        userSearched.value = true;
      } catch (err) {
        userResults.value = [];
        showAlert(errorMessage(err, 'No se pudieron buscar los usuarios.'), 'danger');
      } finally {
        isSearchingUsers.value = false;
      }
    };

    let searchTimer = null;
    watch(userQuery, () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(searchUsers, 400);
    });

    const resetWasiState = () => {
      stopPolling();
      store.commit('appWasi/setCredentials', { connected: false });
      store.commit('appWasi/setImportStatus', { status: 'never' });
      idCompany.value = '';
      wasiToken.value = '';
      importState.value = DEFAULT_STATE;
      importLimit.value = '';
      testResult.value = null;
      confirmDisconnect.value = false;
    };

    const selectUser = (user) => {
      resetWasiState();
      selectedUser.value = user;
      userResults.value = [];
      userQuery.value = '';
      userSearched.value = false;
      init();
    };

    const clearUser = () => {
      resetWasiState();
      selectedUser.value = null;
    };

    onUnmounted(() => {
      clearTimeout(searchTimer);
      stopPolling();
    });

    return {
      selectedUser, userQuery, userResults, isSearchingUsers, userSearched,
      userLabel, selectUser, clearUser,
      idCompany, wasiToken, importState, importLimit,
      importStateOptions, importStateLabel,
      isLoadingInit, isTesting, isSaving, isImporting, isDisconnecting,
      testResult, confirmDisconnect, alert,
      credentials, importStatus, isRunning, progressPct, canSubmit,
      testConnection, saveCredentials, runImport, disconnect,
    };
  },
};
</script>

<style scoped>
.wasi-panel { font-size: 0.85rem; }

/* Selector de usuario */
.wasi-user__chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 10px 14px;
}

.wasi-user__info { display: flex; flex-direction: column; min-width: 0; }
.wasi-user__name { font-size: 0.8rem; font-weight: 600; color: #1E293B; }
.wasi-user__mail { font-size: 0.72rem; color: #64748B; }

.wasi-user__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
}

.wasi-user__list li {
  display: flex;
  flex-direction: column;
  padding: 8px 14px;
  border-bottom: 1px solid #F1F5F9;
  cursor: pointer;
}

.wasi-user__list li:last-child { border-bottom: none; }
.wasi-user__list li:hover { background: #F1F5F9; }

.wasi-empty {
  background: #F8FAFC;
  border: 1px dashed #CBD5E1;
  border-radius: 12px;
  padding: 18px 16px;
  text-align: center;
  font-size: 0.8rem;
  color: #64748B;
}

.wasi-test {
  display: flex;
  align-items: center;
  background: #ECFDF5;
  color: #059669;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.8rem;
}

/* Estado con el que entran los inmuebles */
.wasi-state { display: flex; flex-direction: column; gap: 8px; margin-bottom: 6px; }

.wasi-state__opt {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
}

.wasi-state__opt:hover { background: #F8FAFC; }
.wasi-state__opt input { margin-top: 3px; }
.wasi-state__opt span { display: flex; flex-direction: column; }
.wasi-state__opt strong { font-size: 0.8rem; color: #1E293B; }
.wasi-state__opt small { font-size: 0.72rem; color: #64748B; }

/* Límite de prueba */
.wasi-limit { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.wasi-limit__label { font-size: 0.75rem; font-weight: 600; color: #1E293B; margin: 0; }

.wasi-limit__input {
  width: 96px;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 0.78rem;
  color: #1E293B;
}

.wasi-limit small { flex: 1 1 100%; font-size: 0.72rem; line-height: 1.4; }

/* Datos de conexión */
.wasi-conn {
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  overflow: hidden;
}

.wasi-conn__row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 14px;
  border-bottom: 1px solid #F1F5F9;
}

.wasi-conn__row:last-child { border-bottom: none; }

.wasi-conn__label {
  font-size: 0.75rem;
  color: #64748B;
}

.wasi-conn__value {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1E293B;
  text-align: right;
}

/* Progreso */
.wasi-progress {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 14px 16px;
}

.wasi-progress__text { font-size: 0.8rem; color: #1E293B; font-weight: 600; }
.wasi-progress__pct  { font-size: 0.78rem; color: #2563EB; font-weight: 700; }

.wasi-bar {
  height: 8px;
  background: #E2E8F0;
  border-radius: 20px;
  overflow: hidden;
}

.wasi-bar__fill {
  height: 100%;
  background: linear-gradient(90deg, #0EA5E9, #2563EB);
  border-radius: 20px;
  transition: width 0.4s ease;
}

.wasi-help {
  font-size: 0.75rem;
  color: #64748B;
  line-height: 1.5;
  margin: 0;
}

.wasi-confirm {
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 0.8rem;
  color: #991B1B;
}
</style>
